/**
 * モーダル展開時に shadowHost を `document.body` 直下へ portal するための React Context。
 *
 * 背景:
 *   埋め込み先サイトの祖先要素が `position: relative; z-index: <小>` 等で
 *   stacking context を作っていると、moovy モーダルの z-index がいくら大きくても
 *   その「箱」の中に閉じ込められて、外側の sticky ヘッダー等に勝てない事象が発生する。
 *   shadowHost を `document.body` 直下に移動することで祖先の stacking context から
 *   完全に脱出させ、最大値の z-index で先方サイトの上に出せるようにする。
 *
 * 設計:
 *   - Provider が shadowHost を保持し、`setPortalActive(boolean)` を提供する。
 *   - 子の各プレイヤー（POPUP/SLIDER/TILE）は自身のモーダル開閉に合わせて
 *     setPortalActive を呼ぶだけで、DOM 移動とスタイル復元は Provider が一括管理する。
 *   - Provider が無い場合（テスト等）も hook は null を返すので呼び出し側で安全に分岐できる。
 *
 * 互換性:
 *   - bridge mode: 親に open を委譲するためモーダルは子側では開かれない。
 *     setPortalActive(true) は呼ばれず、本機構は noop となるため bridge と競合しない。
 *   - direct / inline-fallback mode: 本機構が有効化される。
 *   - **iframe 内（STUDIO 等）: portal 自体を行わない**。
 *     STUDIO のような親サイトは iframe の高さを「中身の高さ」に追従させて自動調整する
 *     仕組みを持っており、shadowHost を `position: fixed` 化すると flow content が
 *     消えて iframe が極端に縮む（iframe ＝ 24px 等）副作用が出る。
 *     その結果、モーダルが iframe の極小ビューポート内に押し込まれて平べったく潰れるため、
 *     iframe 内では shadowHost を移動せず、その場で `position: fixed` のモーダルを
 *     開くだけにとどめる（PR #95 以前の挙動と同一）。
 *   - **iframe 埋め込みページ（`/embed/{scenarioId}`、`isFrameEmbed=true`）**: DOM 移動の代わりに
 *     Fullscreen API で iframe 自身を画面全体に広げる。親ページに JavaScript を置かずに
 *     script 埋め込みと同じ「ページ全体を覆うモーダル」を再現するための唯一の手段。
 *     全画面にできない環境（iPhone Safari / allowfullscreen 無し）では従来どおり
 *     iframe 内でモーダルが開く。詳細は `lib/embedding/frameFullscreen.ts`。
 */

import { createContext, useCallback, useContext, useMemo, useRef, type FC, type ReactNode } from 'react'

import { exitFrameFullscreen, requestFrameFullscreen } from '@/lib/embedding/frameFullscreen'

export type ShadowHostPortalContextValue = {
  /**
   * モーダル開閉に応じて shadowHost を body 直下へ portal する／元に戻す。
   * - true: shadowHost を `document.body` の末尾に移動し、フルスクリーンスタイルを適用
   * - false: 元の親要素・元の兄弟位置に戻し、開く直前のスタイルへ復元
   *
   * 既に portal 済の状態で true を渡しても、未 portal の状態で false を渡しても
   * 何もしない（idempotent）。
   */
  setPortalActive: (active: boolean) => void
  /**
   * iframe 埋め込みページ（`/embed/{scenarioId}`）で動作中か。
   *
   * true のとき、モーダルは開いている間だけ iframe を全画面化して表示される。
   * 各モーダルはこれを見て「全画面が外から解除されたら閉じる」「閉じる操作を
   * 縮小再生（PiP）へ逃がさず完全に閉じる」といった frame 特有の振る舞いに切り替える。
   * script 埋め込み（direct / bridge / inline-fallback）では常に false。
   */
  isFrameEmbed: boolean
}

const ShadowHostPortalContext = createContext<ShadowHostPortalContextValue | null>(null)

/**
 * portal 中に shadowHost に適用する z-index。
 * 32bit 整数の最大値で、先方サイトの sticky ヘッダー等が指定し得る現実的な
 * z-index 値（数百〜数十万）を確実に上回る。
 */
export const PORTAL_Z_INDEX = '2147483647'

/**
 * 現在の実行コンテキストが iframe 内かを判定する。
 *
 * `window !== window.parent` で iframe 内かどうかを安全に確認できる。
 * 直接 window.parent を参照するとクロスオリジン制約で例外になるケースがあり得るが、
 * 「自分自身と等しいか」の比較は SOP（Same-Origin Policy）の制約を受けないため安全。
 *
 * iframe 内では portal による shadowHost 移動を行わない（STUDIO の iframe 自動高さ
 * 調整機構と競合するため）。詳細はファイル冒頭の docstring を参照。
 */
const isInIframe = (): boolean => {
  try {
    return window !== window.parent
  } catch {
    // クロスオリジン iframe の親参照で SecurityError 等が発生する可能性に備え、
    // 例外時は「iframe 内である」と扱って portal を抑制する側に倒す。
    return true
  }
}

const applyPortalStyles = (shadowHost: HTMLDivElement) => {
  shadowHost.style.position = 'fixed'
  shadowHost.style.inset = '0'
  shadowHost.style.width = '100%'
  shadowHost.style.height = '100%'
  shadowHost.style.zIndex = PORTAL_Z_INDEX
  shadowHost.style.pointerEvents = 'auto'
}

/** portal で上書きするスタイル。開く直前の値を覚えておき、閉じたときにそのまま戻す。 */
const PORTAL_STYLE_KEYS = ['position', 'inset', 'width', 'height', 'zIndex', 'pointerEvents'] as const
type PortalStyleSnapshot = Record<(typeof PORTAL_STYLE_KEYS)[number], string>

const snapshotStyles = (shadowHost: HTMLDivElement): PortalStyleSnapshot =>
  Object.fromEntries(PORTAL_STYLE_KEYS.map((key) => [key, shadowHost.style[key]])) as PortalStyleSnapshot

/**
 * 開く直前のスタイルへ戻す。インライン型（SLIDER / TILE）なら static の枠、
 * POPUP なら画面全体に固定した最前面の層（applyDisplayStyles が当てたもの）に戻る。
 * 固定値で「インラインの枠」に戻すと、POPUP は閉じたあと最前面の設定を失い、
 * 同じページのワイド型などの下に潜ってしまう。
 */
const restoreStyles = (shadowHost: HTMLDivElement, snapshot: PortalStyleSnapshot) => {
  for (const key of PORTAL_STYLE_KEYS) {
    shadowHost.style[key] = snapshot[key]
  }
}

export type ShadowHostPortalProviderProps = {
  shadowHost: HTMLDivElement | undefined
  /** iframe 埋め込みページで動作しているか。省略時は false（script 埋め込み）。 */
  isFrameEmbed?: boolean
  children: ReactNode
}

export const ShadowHostPortalProvider: FC<ShadowHostPortalProviderProps> = ({
  shadowHost,
  isFrameEmbed = false,
  children,
}) => {
  // 元の親と元の兄弟位置を保存し、復帰時に同じ位置へ戻す。
  // null = 現在 portal していない状態。
  const portalAnchorRef = useRef<{
    parent: Node
    nextSibling: Node | null
    styles: PortalStyleSnapshot
    placeholder: HTMLDivElement | null
  } | null>(null)

  const setPortalActive = useCallback(
    (active: boolean) => {
      if (!shadowHost) return
      // iframe 埋め込みページでは shadowHost を動かさず、iframe 自身を全画面にする。
      // 同じ状態で繰り返し呼ばれても frameFullscreen 側で吸収される（idempotent）。
      if (isFrameEmbed) {
        if (active) {
          requestFrameFullscreen()
        } else {
          exitFrameFullscreen()
        }
        return
      }
      // iframe 内では shadowHost を移動しない。
      // STUDIO のように親サイトが iframe 高さを中身に合わせて自動調整するケースで、
      // shadowHost を position:fixed 化すると iframe の flow content が消えて
      // iframe が極端に縮む（24px 等）→ モーダルが平べったく潰れるという副作用が出るため。
      if (isInIframe()) return

      if (active) {
        // 既に portal 済なら何もしない（多重呼び出し対策）
        if (portalAnchorRef.current) return
        // 元の位置を記録（parentNode が無い場合は portal せずに諦める）
        if (!shadowHost.parentNode) return
        // ページの流れの中にある埋め込み（ワイド型のポスター・SLIDER / TILE の枠）は、抜いた跡に
        // 同じ高さの空き枠を置く。置かないと下の内容が詰まり、モーダルの後ろのページが開く前と変わる
        const position = window.getComputedStyle(shadowHost).position
        const placeholder =
          position === 'fixed' || position === 'absolute' ? null : document.createElement('div')
        if (placeholder) {
          placeholder.style.height = `${shadowHost.getBoundingClientRect().height}px`
          placeholder.setAttribute('aria-hidden', 'true')
          shadowHost.parentNode.insertBefore(placeholder, shadowHost)
        }
        portalAnchorRef.current = {
          parent: shadowHost.parentNode,
          nextSibling: shadowHost.nextSibling,
          styles: snapshotStyles(shadowHost),
          placeholder,
        }
        document.body.appendChild(shadowHost)
        applyPortalStyles(shadowHost)
      } else {
        // 未 portal なら何もしない
        if (!portalAnchorRef.current) return
        const { parent, nextSibling, styles, placeholder } = portalAnchorRef.current
        portalAnchorRef.current = null
        // 空き枠を置いていればその場所へ戻して枠を外す。無ければ元の兄弟の前に挿入し、
        // 元の兄弟が既に居ない場合は末尾に追加。
        if (placeholder?.parentNode) {
          placeholder.parentNode.replaceChild(shadowHost, placeholder)
        } else if (nextSibling && nextSibling.parentNode === parent) {
          parent.insertBefore(shadowHost, nextSibling)
        } else {
          parent.appendChild(shadowHost)
        }
        restoreStyles(shadowHost, styles)
      }
    },
    [shadowHost, isFrameEmbed],
  )

  const value = useMemo<ShadowHostPortalContextValue>(
    () => ({ setPortalActive, isFrameEmbed }),
    [setPortalActive, isFrameEmbed],
  )

  return <ShadowHostPortalContext.Provider value={value}>{children}</ShadowHostPortalContext.Provider>
}

/**
 * portal 制御 API を取得する。Provider 配下でなければ null を返すので、
 * 呼び出し側は `portal?.setPortalActive(...)` のように null-safe に扱う。
 */
export const useShadowHostPortal = (): ShadowHostPortalContextValue | null =>
  useContext(ShadowHostPortalContext)
